// KHIS submissions and quality indicators (docs/plans/phase3-khis-and-quality.md §3). URLs
// carry internal IDs and a YYYYMM period only; reasons travel in bodies.
import { queryOptions } from '@tanstack/react-query';
import {
  khisMappingVersionSchema,
  khisMappingViewSchema,
  khisSubmissionListSchema,
  khisSubmissionSchema,
  qualityDashboardSchema,
  type PrepareKhisSubmissionRequest,
  type QualityIndicatorView,
  type SaveKhisDraftRequest,
} from '@wodi/shared';

import { api } from './client.ts';

export const khisKeys = {
  all: ['khis'] as const,
  mapping: ['khis', 'mapping'] as const,
  submissions: ['khis', 'submissions'] as const,
  submission: (id: string) => ['khis', 'submission', id] as const,
};

export const khisMappingQuery = queryOptions({
  queryKey: khisKeys.mapping,
  queryFn: () => api('GET', '/khis/mapping', { schema: khisMappingViewSchema }),
});

export const khisSubmissionsQuery = queryOptions({
  queryKey: khisKeys.submissions,
  queryFn: () => api('GET', '/khis/submissions', { schema: khisSubmissionListSchema }),
});

/** Refreshed while a send is waiting, so the state changes without a reload. */
export function khisSubmissionQuery(id: string) {
  return queryOptions({
    queryKey: khisKeys.submission(id),
    queryFn: () => api('GET', `/khis/submissions/${id}`, { schema: khisSubmissionSchema }),
    refetchInterval: (query) => (query.state.data?.state === 'waiting' ? 5_000 : false),
  });
}

export function createKhisDraft() {
  return api('POST', '/khis/mapping/drafts', { schema: khisMappingVersionSchema });
}

export function saveKhisDraft(versionId: string, body: SaveKhisDraftRequest) {
  return api('PUT', `/khis/mapping/drafts/${versionId}`, {
    body,
    schema: khisMappingVersionSchema,
  });
}

export function activateKhisDraft(versionId: string) {
  return api('POST', `/khis/mapping/drafts/${versionId}/activate`, {
    schema: khisMappingVersionSchema,
  });
}

export function setKhisOrgUnit(orgUnitUid: string) {
  return api('PUT', '/khis/org-unit', { body: { orgUnitUid } });
}

export function prepareKhisSubmission(body: PrepareKhisSubmissionRequest) {
  return api('POST', '/khis/submissions', { body, schema: khisSubmissionSchema });
}

export function approveKhisSubmission(id: string) {
  return api('POST', `/khis/submissions/${id}/approve`, { schema: khisSubmissionSchema });
}

export function discardKhisSubmission(id: string, reason: string) {
  return api('POST', `/khis/submissions/${id}/discard`, {
    body: { reason },
    schema: khisSubmissionSchema,
  });
}

export function sendKhisSubmissionAgain(id: string) {
  return api('POST', `/khis/submissions/${id}/send-again`, { schema: khisSubmissionSchema });
}

export const qualityKeys = {
  dashboard: (period: string) => ['quality', 'dashboard', period] as const,
};

export function qualityDashboardQuery(period: string) {
  return queryOptions({
    queryKey: qualityKeys.dashboard(period),
    queryFn: () =>
      api('GET', `/quality/indicators?period=${period}`, { schema: qualityDashboardSchema }),
  });
}

export function setQualityTarget(key: QualityIndicatorView['key'], target: number | null) {
  return api('PUT', `/quality/targets/${key}`, { body: { target } });
}
